import {InMemoryCache} from '@apollo/client';
import {PhoneNumberFormat, PhoneNumberUtil} from 'google-libphonenumber';

import {BirthSex, LabSubmission, MeasurementUnit} from '#apollo/schema';
import {convertKilogramToPound, formatAPIDate} from '#util';
import {convertCmToFeetString} from '#util/height';
import {getLifestyleScoreTitle} from '#util/lifestyleScore';

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {},
    },
    MultipleChoiceOption: {
      keyFields: ['id', 'text'],
    },
    // id is a variant id shared across orders. Queries loading orders must select the same item fields.
    OrderItem: {
      keyFields: false,
    },
    SurveyAssessment: {
      fields: {
        lifestyleScoreTitle: {
          read: (_, {readField}) => {
            const lifestyleScore = readField<number>('lifestyleScore');
            return lifestyleScore ? getLifestyleScoreTitle(lifestyleScore) : null;
          },
        },
      },
    },
    CategoryAssessment: {
      fields: {
        categoriesMeetingRequirements: {
          read: (_, {readField}) => {
            const lifestyleScore = readField<number>('lifestyleScore');
            return lifestyleScore ? getLifestyleScoreTitle(lifestyleScore) : null;
          },
        },
      },
    },
    User: {
      fields: {
        hasReceivedLabResults: {
          read: (_, {readField}) => {
            const labSubmissions = readField<LabSubmission[]>('labSubmissions');
            const labSubmissionsWithResults =
              labSubmissions?.filter(labSubmission => labSubmission?.labRecord?.hasResult) || [];
            return labSubmissionsWithResults?.length > 0;
          },
        },
      },
    },
    UserProfile: {
      fields: {
        formattedBirthSex: {
          read(_, {readField}) {
            const birthSex = readField<BirthSex>('birthSex');
            switch (birthSex) {
              case BirthSex.Female:
                return 'Female';
              case BirthSex.Male:
                return 'Male';
              default:
                return 'Unknown';
            }
          },
        },
        formattedBirthday: {
          read(_, {readField}) {
            const birthday = readField<string>('birthday');
            return birthday ? formatAPIDate(birthday) : null;
          },
        },
        formattedPhoneNumber: {
          read(_, {readField}) {
            const phoneNumber = readField<string>('phoneNumber');
            const phoneNumberUtil = PhoneNumberUtil.getInstance();
            const googleFormattedPhoneNumber = phoneNumber
              ? phoneNumberUtil.format(
                  phoneNumberUtil.parse(phoneNumber),
                  PhoneNumberFormat.NATIONAL,
                )
              : undefined;

            return googleFormattedPhoneNumber?.replace(/[\(\)]/g, '').replace(' ', '-');
          },
        },
        formattedWeight: {
          read(_, {readField}) {
            const weight = readField<number>('weight');
            const weightDisplayUnit = readField<MeasurementUnit>('weightDisplayUnit');

            const formattedWeight =
              weightDisplayUnit === MeasurementUnit.Imperial
                ? `${Math.round(convertKilogramToPound(weight))} lb`
                : `${weight} kg`;

            return weight ? formattedWeight : null;
          },
        },
        formattedHeight: {
          read(_, {readField}) {
            const height = readField<number>('height');
            const heightDisplayUnit = readField<MeasurementUnit>('heightDisplayUnit');

            const formattedHeight =
              heightDisplayUnit === MeasurementUnit.Imperial
                ? convertCmToFeetString(height)
                : `${height} cm`;

            return height ? formattedHeight : null;
          },
        },
        initials: {
          read(_, {readField}) {
            const firstName = readField<string>('firstName');
            const lastName = readField<string>('lastName');
            const initials = firstName && lastName ? `${firstName[0]}${lastName[0]}` : '';
            return initials;
          },
        },
        fullName: {
          read(_, {readField}) {
            const firstName = readField<string>('firstName');
            const lastName = readField<string>('lastName');
            const fullName = firstName ? `${firstName} ${lastName}` : '';
            return fullName;
          },
        },
      },
    },
    UserTrend: {
      keyFields: ['labSubmissionId'],
    },
  },
});
